import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import wishlistService from '../services/wishlistService';
import HeartIcon from './HeartIcon';
import './WishlistIcon.css';

/**
 * A reusable wishlist heart icon component that handles its own state
 * 
 * @param {Object} props
 * @param {Object} props.card - The card to toggle in the wishlist
 * @param {boolean} props.disabled - Whether the button should be disabled
 * @param {string} props.className - Additional classes to apply
 * @param {number} props.size - Icon size in pixels (default: 24)
 */
const WishlistIcon = ({ 
  card,
  disabled = false, 
  className = '',
  size = 24
}) => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // Use the card's inWishlist property for the initial state
  const [isInWishlist, setIsInWishlist] = useState(card?.inWishlist || false);

  // Handle wishlist toggle
  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If user is not logged in, redirect to login
    if (!currentUser) {
      navigate('/login', { 
        state: { 
          from: window.location.pathname,
          message: 'Please log in to add items to your wishlist'
        } 
      });
      return;
    }

    if (!card || !card.id) {
      console.error('Invalid card provided to WishlistIcon:', card);
      return;
    }

    // Update local state immediately for responsive UI
    const newWishlistState = !isInWishlist;
    setIsInWishlist(newWishlistState);
      
    try {
      if (isInWishlist) {
        // Remove from wishlist
        const result = await wishlistService.removeFromWishlist(card);
        console.log('Removed from wishlist:', result);
        showToast(`Removed ${card.name} from wishlist`, 'info');
      } else {
        // Add to wishlist
        const result = await wishlistService.addToWishlist(card);
        console.log('Added to wishlist:', result);
        showToast(`Added ${card.name} to wishlist`, 'success');
      }
    } catch (err) {
      console.error('Error toggling wishlist for card:', card.id, err.response ? err.response.data : err.message);
      // Show error toast
      showToast('Failed to update wishlist', 'error');
      // Rollback UI state to previous state
      setIsInWishlist(!newWishlistState); // Revert to previous state
    }
  };

  return (
    <button 
      className={`wishlist-icon-btn ${isInWishlist ? 'active' : ''} ${className}`}
      onClick={handleToggle}
      disabled={disabled}
      title={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
      aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
    >
      <HeartIcon size={size} isActive={isInWishlist} />
    </button>
  );
};

export default WishlistIcon;